import type { ApiClient } from '@acies/platform';
import { Effect } from 'effect';
import type { SearchResult } from '../domain/search-results';
import { runCall } from './runtime';
import { type SearchError, type SearchOptions, searchAt, searchCached, searchUrl } from './search';
import type { EnginePath } from './shared';

/** The resource segment construction models live under, inside the store. */
const resource = 'construction-models';

/** What a construction model search asks for, which is what any search asks for. */
export type SearchConstructionModelsOptions = SearchOptions;

/** Everything searching the store for models can fail with. */
export type SearchConstructionModelsError = SearchError;

/** The calls against the store's construction models. */
export interface ConstructionModelCalls {
	/**
	 * Searches the store's construction models by name.
	 *
	 * Answers with at most `limit` models, so this is what the model picker calls as the user types rather than something to list the catalogue with. The models beyond the limit are not reported: the caller asked for a shortlist and gets one.
	 */
	readonly searchConstructionModels: (options: SearchConstructionModelsOptions) => Effect.Effect<ReadonlyArray<SearchResult>, SearchConstructionModelsError>;
}

/**
 * Implements the construction model calls over the client and store path every call shares.
 *
 * `typeof ApiClient.Service` is the shape `ApiClient` provides — the client with this API's host, bearer token, account header, auth failures and retry policy already applied.
 */
export function makeConstructionModelCalls(client: typeof ApiClient.Service, storePath: EnginePath): ConstructionModelCalls {
	return {
		searchConstructionModels: (options) => storePath(resource).pipe(Effect.flatMap((path) => searchAt(client, searchUrl(path, options)))),
	};
}

/**
 * Searches the store's construction models from outside Effect.
 *
 * The awaitable form of {@link ConstructionModelCalls.searchConstructionModels}, for the model picker, which is a web component rather than an Effect program.
 *
 * All this adds to the general `search` in `services/search.ts` is the one thing it cannot be handed: where the models live. A lookup parameter arrives carrying the path of the collection its value is picked from, so a lookup picker searches that path directly — but nothing tells the model picker where the store is,
 * and the store is the account's default engine, read per call because the host app rewrites it when the user switches account mid-session. Resolving that is the whole of what is left here.
 */
export const searchConstructionModels = (limit: number, name: string): Promise<ReadonlyArray<SearchResult> | SearchConstructionModelsError> => runCall(({ storePath }) => storePath(resource).pipe(Effect.flatMap((path) => searchCached(path, { limit, name }))));
